import { UetInfoProps } from '@/api/uet';
import FixedBottomButton from '@/components/FixedBottomButton';
import { PlusOutlined } from '@ant-design/icons';
import { apiV2UetThermalPowerStationAllPost } from '@maxtropy/device-customer-apis-v2';
import { Button, EllipsisSpan, Modal, Table, useUpdate } from '@maxtropy/components';
import { useRequest } from 'ahooks';
import { Space, Spin } from 'antd';
import { FC, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import DeviceModal from './components/DeviceModal';
import GeneratorSetModal from './components/GeneratorSetModal';
import StationModal from './components/StationModal';
import styles from './index.module.scss';
import { GeneratorUnitItem, ThermalPowerStationItem } from './types';

interface Props {
  info?: UetInfoProps;
  isDetail?: boolean;
}

interface GeneratorSetTableProps {
  station: ThermalPowerStationItem;
  generatorUnits: GeneratorUnitItem[];
  disabled: boolean;
  onChanged: () => void;
}

const GeneratorSetTable: FC<GeneratorSetTableProps> = ({ station, generatorUnits, disabled, onChanged }) => {
  const [currentGeneratorUnit, setCurrentGeneratorUnit] = useState<GeneratorUnitItem>();
  const [generatorModalOpen, setGeneratorModalOpen] = useState(false);
  const [deviceModalOpen, setDeviceModalOpen] = useState(false);

  const closeGeneratorModal = () => {
    setGeneratorModalOpen(false);
    setCurrentGeneratorUnit(undefined);
  };

  const closeDeviceModal = () => {
    setDeviceModalOpen(false);
    setCurrentGeneratorUnit(undefined);
  };

  const columns = [
    {
      title: '序号',
      width: 80,
      render: (_: unknown, __: GeneratorUnitItem, index: number) => index + 1,
    },
    {
      title: '发电机组名称',
      dataIndex: 'name',
      ellipsis: { showTitle: true },
      render: (value?: string) => <EllipsisSpan value={value || '-'} />,
    },
    {
      title: '装机容量',
      dataIndex: 'installedCapacity',
      ellipsis: { showTitle: true },
      render: (value?: number) => <EllipsisSpan value={value === undefined ? '-' : `${value}MW`} />,
    },
    {
      title: '关联设备',
      dataIndex: 'linkedDevice',
      ellipsis: { showTitle: true },
      render: (value?: GeneratorUnitItem['linkedDevice']) => <EllipsisSpan value={value?.name || '-'} />,
    },
    {
      title: '操作',
      width: 200,
      render: (record: GeneratorUnitItem) => (
        <Space size={8}>
          <Button
            type="link"
            disabled={disabled}
            onClick={() => {
              setCurrentGeneratorUnit(record);
              setGeneratorModalOpen(true);
            }}
          >
            编辑
          </Button>
          <Button
            type="link"
            disabled={disabled}
            onClick={() => {
              setCurrentGeneratorUnit(record);
              setDeviceModalOpen(true);
            }}
          >
            关联设备
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Button
        type="dashed"
        ghost
        disabled={disabled}
        wrapStyle={{ marginBottom: 10 }}
        onClick={() => {
          setCurrentGeneratorUnit(undefined);
          setGeneratorModalOpen(true);
        }}
      >
        <PlusOutlined />
        新建发电机组
      </Button>
      <Table rowKey="id" dataSource={generatorUnits} columns={columns} pagination={false} />
      {generatorModalOpen && (
        <GeneratorSetModal
          open={generatorModalOpen}
          station={station}
          generatorUnit={currentGeneratorUnit}
          onCancel={closeGeneratorModal}
          onSuccess={() => {
            closeGeneratorModal();
            onChanged();
          }}
        />
      )}
      {deviceModalOpen && currentGeneratorUnit && (
        <DeviceModal
          open={deviceModalOpen}
          station={station}
          generatorUnit={currentGeneratorUnit}
          onCancel={closeDeviceModal}
          onSuccess={() => {
            closeDeviceModal();
            onChanged();
          }}
        />
      )}
    </>
  );
};

const ThermalPowerStation: FC<Props> = ({ info, isDetail }) => {
  const { id } = useParams<{ id: string }>();
  const [update, forceUpdate] = useUpdate();
  const [edit, setEdit] = useState(false);
  const [stationModalOpen, setStationModalOpen] = useState(false);
  const [currentStation, setCurrentStation] = useState<ThermalPowerStationItem>();
  const disabled = !!isDetail || !edit;

  const { data, loading } = useRequest(
    () => apiV2UetThermalPowerStationAllPost({ id: Number(id) }).then(res => res.list ?? []),
    {
      ready: !!id && !!info?.mcid,
      refreshDeps: [id, info?.mcid, update],
    }
  );

  const closeStationModal = () => {
    setStationModalOpen(false);
    setCurrentStation(undefined);
  };

  return (
    <div className={styles.thermalPowerStation}>
      <Button
        type="primary"
        disabled={disabled}
        onClick={() => {
          setCurrentStation(undefined);
          setStationModalOpen(true);
        }}
      >
        <PlusOutlined />
        新建火电站
      </Button>

      <Spin spinning={loading}>
        <div className={styles.stationList}>
          {(data ?? []).map(item => {
            const station = item.thermalPowerStation;
            if (!station) return null;

            return (
              <section className={styles.stationPanel} key={station.id}>
                <header className={styles.stationHeader}>
                  <div className={styles.stationInfo}>
                    <div className={styles.stationName} title={station.name}>
                      {station.name || '-'}
                    </div>
                    <div className={styles.stationMeta}>
                      <span className={styles.metaLabel}>编号：</span>
                      <span className={styles.metaValue} title={station.code}>
                        {station.code || '-'}
                      </span>
                    </div>
                    <div className={styles.stationMeta}>
                      <span className={styles.metaLabel}>运营单元：</span>
                      <span className={styles.metaValue} title={station.ou?.name}>
                        {station.ou?.name || '-'}
                      </span>
                    </div>
                  </div>
                  <Button
                    type="link"
                    disabled={disabled}
                    onClick={() => {
                      setCurrentStation(station);
                      setStationModalOpen(true);
                    }}
                  >
                    编辑
                  </Button>
                </header>
                <div className={styles.stationContent}>
                  <GeneratorSetTable
                    station={station}
                    generatorUnits={item.generatorUnits ?? []}
                    disabled={disabled}
                    onChanged={forceUpdate}
                  />
                </div>
              </section>
            );
          })}
        </div>
      </Spin>

      <FixedBottomButton>
        {edit ? (
          <Space size={8}>
            <Button type="primary" onClick={() => setEdit(false)}>
              保存
            </Button>
            <Button
              onClick={() => {
                Modal.confirm({
                  title: '这样做会造成未保存的信息丢失，确定要这样做吗？',
                  onOk: () => {
                    forceUpdate();
                    setEdit(false);
                  },
                });
              }}
            >
              取消
            </Button>
          </Space>
        ) : isDetail ? (
          <Button type="primary">
            <Link to="/energy/basic/uet">返回列表</Link>
          </Button>
        ) : (
          <Button type="primary" onClick={() => setEdit(true)}>
            编辑
          </Button>
        )}
      </FixedBottomButton>

      {stationModalOpen && (
        <StationModal
          open={stationModalOpen}
          station={currentStation}
          ouList={info?.ous}
          onCancel={closeStationModal}
          onSuccess={() => {
            closeStationModal();
            forceUpdate();
          }}
        />
      )}
    </div>
  );
};

export default ThermalPowerStation;
